import React, { useState } from "react";
import styles from "./ChatInterface.module.css";

/**
 * TRAC-497: the "thinking" loading bubble (spinning-tire animation + a rotating
 * status phrase) extracted from ChatInterface. Pure presentational — the parent
 * still owns the `isLoading` gate and passes the fade-out state. Shares
 * ChatInterface.module.css so the existing class names/animations are unchanged.
 */

const PROCESSING_PHRASES = [
  "Wheels are turning...",
  "Rolling on it...",
  "Getting traction...",
  "Spinning up results...",
  "Treading through the options...",
];

// Pick one random phrase per mount (i.e., per message) — no cycling within the same load
const ProcessingText: React.FC = () => {
  const [phrase] = useState(
    () => PROCESSING_PHRASES[Math.floor(Math.random() * PROCESSING_PHRASES.length)]
  );

  return (
    <span className={styles.processingText}>
      {phrase}
    </span>
  );
};

interface ThinkingIndicatorProps {
  /** Fades the indicator out as the first real chunk arrives. */
  isFadingOut: boolean;
}

const ThinkingIndicator: React.FC<ThinkingIndicatorProps> = ({ isFadingOut }) => {
  return (
    <div className={`${styles.messageWrapper} ${styles.assistantMessage}`}>
      <div className={styles.messageHeader} />
      <div className={styles.messageContent}>
        <div
          className={`${styles.processingIndicator} ${isFadingOut ? styles.processingIndicatorFadeOut : ""}`}
        >
          <div className={styles.tireAnimation}>
            <svg
              className={styles.tireSvg}
              viewBox="0 0 100 100"
              xmlns="http://www.w3.org/2000/svg"
            >
              {/* Outer tire ring */}
              <circle
                cx="50"
                cy="50"
                r="45"
                fill="none"
                stroke="#2b7088"
                strokeWidth="8"
                className={styles.tireOuter}
              />
              {/* Tread pattern - multiple lines around the tire */}
              <g className={styles.treadPattern}>
                {[...Array(12)].map((_, i) => (
                  <line
                    key={i}
                    x1="50"
                    y1="5"
                    x2="50"
                    y2="12"
                    stroke="#2b7088"
                    strokeWidth="3"
                    strokeLinecap="round"
                    transform={`rotate(${i * 30} 50 50)`}
                  />
                ))}
              </g>
              {/* Inner hub */}
              <circle
                cx="50"
                cy="50"
                r="18"
                fill="#2b7088"
                className={styles.tireHub}
              />
              {/* Hub spokes */}
              <g className={styles.hubSpokes}>
                {[...Array(5)].map((_, i) => (
                  <circle
                    key={i}
                    cx={50 + 10 * Math.cos((i * 72 * Math.PI) / 180)}
                    cy={50 + 10 * Math.sin((i * 72 * Math.PI) / 180)}
                    r="3"
                    fill="#ffffff"
                  />
                ))}
              </g>
              {/* Center cap */}
              <circle cx="50" cy="50" r="5" fill="#ffffff" />
            </svg>
          </div>
          <ProcessingText />
        </div>
      </div>
    </div>
  );
};

export default ThinkingIndicator;
